import {
  MessageConfig,
  MessageType,
  NotificationConfig,
  NotificationType,
} from '@routific/hive';
import i18n from '../../i18n';

type SkippedShopifyOrder = {
  reason?: string;
};

const ADDRESS_TOO_LONG_REASON = 'address_too_long';

export const getShopifyImportLoadingMessageKey = (projectId: string): string =>
  `shopify-import-pending-${projectId}`;

const hasAddressTooLongSkippedOrder = (
  skippedOrders: SkippedShopifyOrder[] = [],
): boolean =>
  skippedOrders.some((order) => order.reason === ADDRESS_TOO_LONG_REASON);

export const shopifyImportLoadingMessage = (
  projectId: string,
): MessageConfig => ({
  content: String(i18n.t('shopify:ordersNotifications.loading')),
  key: getShopifyImportLoadingMessageKey(projectId),
  type: MessageType.LOADING,
  duration: 0,
  dataCy: 'shopify-import-loading-message',
});

export const shopifyImportSuccessNotification = (
  createdCount: number,
): NotificationConfig => ({
  id: 'shopify-import-success',
  type: NotificationType.SUCCESS,
  message: String(i18n.t('shopify:ordersNotifications.success', { createdCount })),
  dataCy: 'shopify-import-success-notification',
});

export const shopifyImportPartialSuccessNotification = (
  createdCount: number,
  totalCount: number,
  skippedOrders: SkippedShopifyOrder[] = [],
): NotificationConfig => ({
  id: 'shopify-import-partial',
  type: NotificationType.WARNING,
  message: hasAddressTooLongSkippedOrder(skippedOrders)
    ? String(i18n.t('shopify:ordersNotifications.partialSuccessAddressTooLong', { createdCount, totalCount }))
    : String(i18n.t('shopify:ordersNotifications.partialSuccess', { createdCount, totalCount })),
  dataCy: 'shopify-import-partial-notification',
});

export const shopifyImportAllSkippedNotification = (
  skippedOrders: SkippedShopifyOrder[] = [],
): NotificationConfig => ({
  id: 'shopify-import-skipped',
  type: NotificationType.ERROR,
  message: hasAddressTooLongSkippedOrder(skippedOrders)
    ? String(i18n.t('shopify:ordersNotifications.addressTooLong'))
    : String(i18n.t('shopify:ordersNotifications.allSkipped')),
  dataCy: 'shopify-import-skipped-notification',
});

export const shopifyImportConnectionRevokedNotification =
  (): NotificationConfig => ({
    id: 'shopify-import-connection-revoked',
    type: NotificationType.ERROR,
    message: String(i18n.t('shopify:ordersNotifications.connectionRevoked')),
    dataCy: 'shopify-import-connection-revoked-notification',
  });

export const shopifyImportRateLimitedNotification =
  (): NotificationConfig => ({
    id: 'shopify-import-rate-limited',
    type: NotificationType.ERROR,
    message: String(i18n.t('shopify:ordersNotifications.rateLimited')),
    dataCy: 'shopify-import-rate-limited-notification',
  });

export const shopifyImportErrorNotification = (): NotificationConfig => ({
  id: 'shopify-import-error',
  type: NotificationType.ERROR,
  message: String(i18n.t('shopify:ordersNotifications.error')),
  dataCy: 'shopify-import-error-notification',
});
